import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import React, { useEffect, useState } from 'react';
import { Flex, Box, Text, Image } from 'rebass';
import TextField from 'app/form/formik/text/index';
import { useWindowSize } from 'react-use';
import store from 'store';
import { LoginSection } from './Wrapper';
import useMatch from 'app/hooks/match';
import { useCreateNewAccount, useCreateNewV3Account, useLogin } from 'auth/hooks';
import { useHistory, useLocation, Link as RouterLink } from 'react-router-dom';
import { postMemberEvent, useGetMemberSWR } from 'members/api';
import useSendToGTM from 'utils/googleTagManager';
import { useMobileCheckHook } from 'story-media/hooks';
import { useCurrentMember } from 'members/hooks/index';
import { getAuthorizedHeaders, getSWR, patch } from 'utils/api';
import Banner from 'app/payment/Banner';
import apiBase from 'utils/api-base';
import { IoMdClose, IoMdCheckmark, IoMdGift } from "react-icons/io";
import { getPrefixedEventName } from 'app/form/helper';
import Wrapper from 'gift-certificate/Wrapper';
import { getChildJournal, postChildJournal } from 'child-journals/api';
import { updateEntity } from 'entities/actions';
import { useDispatch } from 'react-redux';
import { togglefirstTimeUserFlowVisible } from 'stories/actions';
import signupGraphic from './assets/signup-page-graphic.png';
import Reviews from './Reviews';
import reviewerImage1 from './assets/reviewer-image1.png';
import reviewerImage2 from './assets/reviewer-image2.png';
import reviewerImage3 from './assets/reviewer-image3.png';
import reviewerImage4 from './assets/reviewer-image4.png';
import reviewerImage5 from './assets/reviewer-image5.png';
import { TextFieldWrap, Label, ErrorMessageWrap } from 'app/form/formik/text/styled';
import { getOrderId } from 'auth/member';
import { getGiftCertificates } from 'account-dashboard/api';

const userReviews = [{
    review: 'I was thrilled to get a Baby Yearbooks gift — it’s already turning into a beautiful collection I can’t wait to see in print!',
    reviewer: {
        name: 'Tanya, R.',
        image: reviewerImage4
    }
},
{
    review: `I love that my kids will one day watch this book and see special moments with their grandparents come to life. It means so much.`,
    reviewer: {
        name: 'Janet, D.',
        image: reviewerImage5
    }
},
{
    review: 'As a busy mom, it’s been easy to capture Ellie’s firsts: her smiles, giggles, and even her messy spaghetti night. Absolutely priceless.',
    reviewer: {
        name: 'Emily, K.',
        image: reviewerImage1
    }
},
{
    review: 'I love building the book bit by bit — it only takes minutes each time. The reminders really help, especially when the photos are still fresh!',
    reviewer: {
        name: 'Daria, Z.',
        image: reviewerImage2
    }
},
{
    review: `Rereading our son's First Year book brought back priceless memories — Baby Yearbooks makes it effortless!`,
    reviewer: {
        name: 'Martin, S.',
        image: reviewerImage3
    }
},]

const baseUrl = apiBase.replace('/v2', '');

export const label = (text, isRequired) => <Text
    sx={{
        color: "codGrey",
        fontFeatureSettings: 'liga off, calt off',
        fontFamily: 'Poppins',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: '20px', /* 142.857% */
        letterSpacing: '-0.084px',
    }}> {text} {isRequired && <span style={{ color: 'red' }}>*</span >}</Text >


export const FieldText = ({ name, type, disabled, placeholder, ...props }) => {
    return <Field
        component={TextField}
        name={name}
        type={type}
        showError
        placeholder={placeholder}
        m="auto"
        mb={[2, 4]}
        borderRadius='31px'
        sx={{
            width: 'inherit',
            borderColor: '#D9D9D9',
            cursor: disabled && 'not-allowed',
            borderRadius: '31px'
        }}
        {...props}
    />
}

// `onDone` — set by flows that own their own post-auth navigation: the Shopify
// purchase onboarding (account-dashboard/shopify-onboarding) and the gift
// redemption flow (see the /redeem-gift-certificate branch in ./index.js). When
// present it means "this account is ready; hand navigation back to the caller",
// and it fires after BOTH login and signup. It also suppresses the journal-based
// redirects below, which would otherwise send the user to /child-journals/:id
// mid-flow. The gift `claim` above still runs first either way.
//
// `initialEmail` — prefills the email field while leaving it editable. Distinct
// from `usersEmail`, which renders the address as read-only text (payment
// providers such as Apple Pay can supply a wrong address, so the purchase flow
// needs it correctable).
const SignUp = ({ isForMyBaby, usersEmail, disabledEmail, isForWMG, hideSkip, attributes, journalId, onStepDown, redemptionStatus, onStepUp, isLogin = true, onLoginPress, redeemCode, setCurrentChildJournalId,
    setSigningUp,
    isSigningUp,
    onDone,
    initialEmail,
    initialEmailFirstName,
    initialEmailLastName
}) => {
    const { params: query } = useMatch();
    const { push } = useHistory();
    const { pathname } = useLocation();
    const isFromMobile = useMobileCheckHook()
    const [polling, setPolling] = useState(false);
    const dispatch = useDispatch();

    const [showReviews, setShowReviews] = useState(!!isFromMobile && pathname.includes('/get-started'));
    const [showLogin, setShowLogin] = useState(isLogin)
    const [giftCertificate, setGiftCertificate] = useState(null);
    const [showGiftcard, setShowGiftcard] = useState(null);

    const [data, setData] = useState({});
    const [status, setStatus] = useState({ type: '', message: '' });

    const { width } = useWindowSize();
    const member = useCurrentMember()
    const createNewUser = useCreateNewAccount();

    const { revalidate } = useGetMemberSWR('me', {
        revalidateOnFocus: false,
        shouldRetryOnError: false,
    });

    useEffect(() => {
        let interval;
        if (polling && !(attributes?.['has-baby-year-books-subscription'] || attributes?.['has-watch-me-grow-subscription'] || attributes?.['free-trial'] || attributes?.paid)) {
            const checkPaymentStatus = async () => {
                try {
                    const response = await getChildJournal(journalId);

                    if (response.data?.initial?.data?.attributes?.['has-baby-year-books-subscription'] || response.data?.initial?.data?.attributes?.['has-watch-me-grow-subscription'] || response.data?.initial?.data?.attributes?.['free-trial'] || response.data?.initial?.data?.attributes?.paid) {
                        setPolling(false);
                        dispatch(
                            updateEntity({
                                entityType: 'child-journals',
                                attrs: { paid: true },
                                id: journalId,
                            })
                        );
                        let redirectURL = `/child-journals/${journalId}`;

                        if (response.data?.initial?.data?.attributes?.['story-count'] === 0)
                            dispatch(togglefirstTimeUserFlowVisible(true))
                        if (setSigningUp) setSigningUp(false)
                        if (isForWMG && setCurrentChildJournalId) {
                            onStepUp(journalId)
                        }
                        else
                            push(redirectURL)
                    }
                } catch (error) {
                    console.error('Error checking payment status:', error);
                }
            };

            interval = setInterval(checkPaymentStatus, 2000); // Check every 5 seconds
        }

        return () => clearInterval(interval);
    }, [polling, journalId, attributes?.['has-baby-year-books-subscription'], attributes?.['has-watch-me-grow-subscription']]);

    async function createNewUserRequest() {
        let orderId = getOrderId()
        if (!orderId && !store.get('auth_token'))
            await createNewUser();
        // if (firePostMemberEvent) {
        //     if (isFromMobile && pathname.includes('/get-started')) {
        //         firePostMemberEvent('testimonials')
        //     }
        // }
    }

    function goToOnboarding(firstJournal) {
        if (firstJournal) {
            let redirectURL = firstJournal?.attributes?.state !== "initialized" ?
                `/account/${hasUnassigned ? 'unassigned' : 'my-books'}`
                : `/child-journals/${firstJournal.id}`;
            push(redirectURL)
        }
        else
            push('/account')
    }

    async function getChildJournals() {
        const { data, status } = await getSWR(`/v3/child_journals`);
        const { data: giftCertificatesData } = await getGiftCertificates();

        if (status === 200) {
            const childJournals = data?.data
            const giftCertificates = giftCertificatesData?.initial?.data
            const giftedJournalIds = new Set(
                giftCertificates
                    .map(({ relationships }) => relationships?.['child-journal']?.data?.id)
                    .filter(Boolean)
            );
            revalidate();
            if (childJournals?.length) {
                const validJournals = childJournals
                    .filter(({ id, attributes }) => {
                        const subjectName = attributes?.['subject-name']?.toLowerCase() || '';

                        return (
                            !subjectName.includes('tap for intro') &&
                            !subjectName.includes('sample child journal') &&
                            !giftedJournalIds.has(id)
                        );
                    })
                    .sort(
                        (a, b) =>
                            new Date(b.attributes?.['updated-at']) -
                            new Date(a.attributes?.['updated-at'])
                    );

                const firstJournal = validJournals.filter(
                    ({ attributes }) =>
                        attributes?.state === 'initialized'
                )[0];

                const hasFMBSubscription = validJournals.some(
                    ({ attributes }) =>
                        attributes?.['has-baby-year-books-subscription'] ||
                        attributes?.['has-watch-me-grow-subscription'] ||
                        attributes?.['free-trial'] ||
                        attributes?.paid
                );

                const unassignedJournal = validJournals.find(
                    ({ attributes }) => attributes?.state === 'unassigned'
                );

                const myBookJournal = validJournals.find(
                    ({ attributes }) =>
                        attributes?.state === 'initialized' ||
                        attributes?.state === 'assigned'
                );

                const latestJournal = validJournals.filter(
                    ({ attributes }) =>
                        attributes?.state === 'initialized'
                )[0];

                if (hasFMBSubscription && latestJournal) {
                    // Account Type 2 - FMB
                    if (latestJournal?.attributes?.state === 'initialized') {
                        if (setCurrentChildJournalId) {
                            setCurrentChildJournalId(latestJournal.attributes?.id);
                        }

                        if (setSigningUp) setSigningUp(false);

                        if (onStepUp) {
                            onStepUp(latestJournal.attributes?.id);
                        } else {
                            goToOnboarding(latestJournal);
                        }
                    } else {
                        if (setSigningUp) setSigningUp(false);

                        if (latestJournal?.attributes?.['story-count'] === 0) {
                            dispatch(togglefirstTimeUserFlowVisible(true));
                        }

                        push(`/child-journals/${latestJournal.id}`);
                    }
                } else {
                    // Account Type 1 - Purchase Allocation Only
                    let redirectURL;

                    // if (unassignedJournal) {
                    // Case 1: Unallocated books
                    redirectURL = '/account/unassigned';
                    // } else if (myBookJournal) {
                    //     // Case 2 & 4: My Books exists
                    //     redirectURL = '/account/my-books';
                    // } else {
                    //     // Case 3: Gifts
                    //     redirectURL = '/account/gifts';
                    // }

                    if (setSigningUp) setSigningUp(false);

                    if (firstJournal?.attributes?.['story-count'] === 0) {
                        dispatch(togglefirstTimeUserFlowVisible(true));
                    }

                    push(redirectURL);
                }
            }
            else {
                // goToOnboarding()
            }
        }
    }
    useEffect(() => {
        // `onDone` callers own post-auth navigation — don't redirect into a journal.
        if (member?.['has-username-password-identifier'] && showLogin && !onDone) {
            getChildJournals()
        }
    }, [member?.['has-username-password-identifier'], showLogin])

    useEffect(() => {
        if (member?.['has-username-password-identifier']) {
            if (!pathname.includes('/get-started') && !onDone)
                getChildJournals()
        }
        else {
            createNewUserRequest()
        }
    }, [member?.['has-username-password-identifier']])

    useEffect(() => {
        if (pathname !== '/')
            setShowLogin(isLogin)
    }, [isLogin, pathname])

    const login = useLogin();
    const createNewAccount = useCreateNewV3Account()
    const sendToGTM = useSendToGTM();

    // const onErrorHandle = () => {
    //     if (err.response.status === 400) {
    //         store.set(
    //             'buzzCodeError',
    //             err.response?.data?.errors[0]?.detail || 'Buzzcode has already been applied'
    //         );
    //     }
    //     if (err.response.status === 410) {
    //         store.set(
    //             'buzzCodeError',
    //             err.response?.data?.errors[0]?.detail || 'Buzzcode doesnot exist'
    //         );
    //     }
    // }
    const requestOptions = () => ({
        method: 'GET',
        headers: getAuthorizedHeaders(),
    });

    async function getCouponDetails(code = null) {
        setGiftCertificate(code)
        const resp = await fetch(`${baseUrl}/v3/commerce/gift_certificates/${code}`, requestOptions())
        const response = await resp.json();

        if (response?.data === null && response?.status !== 200) {
            setStatus({ type: 'error', message: 'Your gift certificate has been used or is unrecognized', subTitle: <>Please double check your code and try again. If you're still experiencing problems, please <Text as="a" href="mailto:hello@babyyearbooks.com" target="_blank" sx={{ textDecorationLine: 'underline', cursor: 'pointer', fontWeight: 500 }} color="inherit">contact us</Text></> })
            return false
        }
        setStatus({ type: 'success', message: "Gift card applied successfully", subTitle: "You're all set to preserve and celebrate your little one's firsts" })
        return true
    }

    const onSubmit = async (values, { setFieldError, setSubmitting }) => {
        const { 'gift-certificate': giftCertificate } = values
        const newvalues = { ...values, ...query };
        if (query?.['private-code']) newvalues.promotion_code = query?.['private-code'];
        setSubmitting(true);
        if (setSigningUp) setSigningUp(true)
        try {

            if (showLogin)
                await login(values);
            else
                await createNewAccount(values);

            // if (redeemCode || (giftCertificate && status?.type === 'success'))
            //     await patch(`/v3/commerce/gift_certificates/${redeemCode || giftCertificate}`)

            const gtmObject = showLogin
                ? {
                    event: 'bfy.signin',
                    email: values.email,
                }
                : {
                    event: 'bfy.registered',
                    firstName: values['first_name'],
                    lastName: values['last_name'],
                    email: values.email,
                };

            sendToGTM(gtmObject);

            if (!showLogin && !onStepUp && !onDone)
                postMemberEvent(getPrefixedEventName('tell_us_about_baby', width < 600))

            // if (redeemCode)
            //     await patch(`/v3/commerce/gift_certificates/${redeemCode}/claim`)
            if (onDone) {
                // The caller owns what happens next (Shopify onboarding merges the
                // purchase; gift redemption moves to "Open my gift"). Fires for
                // login AND signup — the branch below only calls onStepUp for signup.
                revalidate()
                setSubmitting(false);
                onDone()
            } else {
                if (pathname === '/' || pathname.includes('index.html') || showLogin) {
                    revalidate()
                    // revalidateJournals()
                    // push(redirectURL);
                }
                else {
                    revalidate()
                    if (isForWMG) {

                        const cjRes = await postChildJournal({});

                        const [id] = cjRes.data.result['child-journals'];
                        onStepUp(id)
                    }
                    else
                        onStepUp()
                }
                setSubmitting(false);
            }
        } catch (err) {
            setSubmitting(false);

            const errors = [];
            if (err && err.response && err.response.data && JSON.parse(err.response.data).errors) {
                JSON.parse(err.response.data).errors.forEach((error) => {
                    if (error.detail && error.detail.includes('Invalid Password')) {
                        setFieldError('password', 'Invalid Password')
                    }
                    else if (error.detail && error.detail.includes('Account Exists')) {
                        setFieldError('email', 'An account with this email already exists')

                        errors.push('An account with this email already exists');
                    }
                    else if (error.detail && error.detail.includes('Account Not Found')) {
                        setFieldError('email', 'Account Not Found')

                        errors.push('An account with this email already exists');
                    }
                    else {
                        const parsedErrorDetails = JSON.parse(error.detail)
                        const { message } = parsedErrorDetails
                        setFieldError('password', message);
                    }
                });
            }
        }
    }

    function setInitialValues() {
        if (showLogin)
            return {
                'gift-certificate': giftCertificate || '',
                email: usersEmail || initialEmail || '',
                password: '',
            }

        return {
            'gift-certificate': giftCertificate || '',
            'first_name': initialEmailFirstName || member?.firstName || '',
            'last_name': initialEmailLastName || member?.lastName || '',
            email: usersEmail || initialEmail || member?.email || '',
            password: '',
        }
    }

    function setShowValidationSchema() {
        if (showLogin)
            return Yup.object().shape({
                email: Yup.string().email().required(),
                password: Yup.string().min(6).required(),
            })

        return Yup.object().shape({
            'first_name': Yup.string().required(),
            'last_name': Yup.string().required(),
            email: Yup.string().email().required(),
            password: Yup.string().min(6).required(),
        })
    }

    function title() {
        if (showLogin) return "Login To Your Account"
        return "Let's Get You Set Up"
    }

    function nextButtonText() {
        if (showLogin) return "Sign In"
        return "Sign Up"
    }

    const toggleLogin = onLoginPress ? onLoginPress : () => {
        if (pathname === '/' && showLogin && !onStepUp)
            postMemberEvent(getPrefixedEventName('create_account', width < 600))
        setShowLogin(!showLogin)
    }

    if (!data && member)
        return null;


    return (
        <Formik
            initialValues={setInitialValues()}
            enableReinitialize
            validationSchema={setShowValidationSchema()}
            onSubmit={onSubmit}
        >
            {({ values, errors, isSubmitting, handleSubmit, setFieldValue }) => {
                const SignUpText = isSubmitting || isSigningUp ? "Creating your account" : "Complete My Account"

                return (
                    <Form>
                        <Wrapper
                            hideSkip={hideSkip}
                            onSubmit={handleSubmit}
                            isDisabled={isSubmitting || isSigningUp}
                            showFooter
                            onBack={onStepDown}
                            isSubmitting={isSubmitting || isSigningUp}
                            percentComplete={0}
                            bg="aliceBlue"
                            nextButtonText={showLogin ? 'Sign In' : SignUpText}
                        >
                            <Flex flexDirection={["column", "column", "column", "row"]} justifyContent="center" width="100%" >
                                <Flex flexDirection="column" bg="#F9F6FF" overflow="hidden" mr={[0, 0, 4]} height={["auto", "auto", "auto", "100vh"]} width={["100%", "100%", "100%", '50vw']} display="flex" justifyContent="center" alignItems="center" >
                                    <Image width="100%"
                                        sx={{ display: ["none", "block", "block", "block"] }}
                                        src={signupGraphic} maxHeight={['auto', 'auto', 'auto', 429]} maxWidth={['auto', 300, 300, 600]} />
                                    <Box bg="#F9F6FF" mb="6.7%" sx={{ display: ["none", "none", "none", "block"] }}>
                                        <Reviews reviews={userReviews} />
                                    </Box>
                                </Flex>
                                <Box width={["95%", "100%"]} maxWidth="600px" mx="auto">
                                    {((redemptionStatus || status)?.message) && (
                                        <Banner
                                            bg={((redemptionStatus || status).type === 'success' || redeemCode) ? 'successBg' : 'pinks.1'}
                                            color={((redemptionStatus || status).type === 'success' || redeemCode) ? 'success' : 'pinks.5'}
                                            heading={(redemptionStatus || status).message || "Gift card applied successfully"}
                                            text={(redemptionStatus || status).subTitle || "You're all set to preserve and celebrate your little one's firsts"}
                                            p="16px"
                                            size={'small'}
                                            my={2}
                                        />
                                    )}
                                    <Box >
                                        <Flex flexDirection="column" m="auto">
                                            {/* <Text
                                            sx={{
                                                color: "#34495E",
                                                textAlign: "center",
                                                fontFamily: "Poppins",
                                                fontSize: "15px",
                                                fontStyle: "normal",
                                                fontWeight: "400",
                                                lineHeight: "150%",
                                                letterSpacing: "-0.3px",
                                            }}
                                        >
                                            {showLogin ? 'Welcome Back!' : 'Step 1 of 3'}
                                        </Text> */}
                                            {/* {pathname.includes('get-started') && !isFromMobile && <IoIosArrowRoundBack size={30} style={{ cursor: 'pointer' }} onClick={onStepDown} />} */}
                                            <Text
                                                color="codGrey"
                                                textAlign="left"
                                                fontFamily="Bitter"
                                                fontSize={[24, 28]}
                                                lineHeight={["34px", "38px"]}
                                                letterSpacing="-0.6"
                                                mt={2}
                                                mb={1}
                                            >
                                                {title()}
                                            </Text>
                                            {!showLogin && <Text
                                                color="#64748B"
                                                fontFamily="Poppins"
                                                fontSize={16}
                                                lineHeight="150%"
                                                letterSpacing="-0.32px"
                                                mt={2}
                                                mb={1}
                                            >
                                                You'll be able to send this as a gift or start your own baby book next.
                                            </Text>}

                                            <Box mb={3}>
                                                <LoginSection noRedeem onLoginPress={toggleLogin} showLogin={showLogin} />
                                            </Box>
                                            {/* {(!redeemCode && !isForMyBaby) && (showGiftcard ? <Flex sx={{ position: 'relative' }}>
                                                {status?.type &&
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        marginTop: '38px',
                                                        marginLeft: '10px',
                                                    }}>
                                                        {status?.type === 'error' ?
                                                            <Box as={IoMdClose} color="#471E08" size={24} bg="#FFDCC8" sx={{ borderRadius: '50%', p: '4px' }} /> :
                                                            <Box as={IoMdCheckmark} color="#112A09" size={24} bg="#C2F1A5" sx={{ borderRadius: '50%', p: '4px' }} />}
                                                    </Box>}
                                                <Box as={IoMdClose} onClick={() => {
                                                    setFieldValue('gift-certificate', '')
                                                    setStatus({ type: '', message: '' })
                                                    setShowGiftcard(!showGiftcard)
                                                }} color="#fff" size={24} bg="#000" sx={{
                                                    cursor: 'pointer',
                                                    borderRadius: '50%', p: '4px', position: 'absolute',
                                                    right: 0
                                                }} />
                                                <FieldText
                                                    name="gift-certificate"
                                                    placeholder="Enter gift certificate"
                                                    width="100%"
                                                    label={label('Gift Certificate')}
                                                    onBlur={(e) => !!e.target.value && getCouponDetails(e.target.value)}
                                                    mt={0}
                                                    pl={status?.type && "40px"}
                                                />
                                            </Flex> :
                                                <Flex maxWidth="max-content" mx="auto" justifyContent="center" alignItems="center" mb={2} onClick={() => setShowGiftcard(true)} sx={{ cursor: 'pointer' }}>
                                                    <Box as={IoMdGift} color="#43115A" size={30} mr={1} bg="#F5EBFC" sx={{ borderRadius: '50%', p: '6px' }} />
                                                    <Text sx={{ textDecorationLine: 'underline' }}>Redeem a gift certificate</Text>
                                                </Flex>)} */}
                                            {usersEmail ?
                                                <TextFieldWrap mb={[2, 4]}>
                                                    <Label >{label('Your email', true)}</Label>
                                                    <Text
                                                        pt="12px"
                                                        sx={{
                                                            color: '#5E5E5E',
                                                            fontFeatureSettings: "'liga' off, 'calt' off",
                                                            fontFamily: 'Poppins',
                                                            fontSize: '16px',
                                                            fontStyle: 'normal',
                                                            fontWeight: '400',
                                                            lineHeight: '10px',
                                                            letterSpacing: '-0.096px'
                                                        }}
                                                    >
                                                        {values.email}
                                                    </Text>
                                                    {errors?.email && (
                                                        <ErrorMessageWrap maxWidth="max-content" fontSize={16}>
                                                            {errors.email}
                                                        </ErrorMessageWrap>
                                                    )}
                                                </TextFieldWrap>
                                                : <FieldText
                                                    name="email"
                                                    label={label('Your email', true)}
                                                    width="100%"
                                                    placeholder="Enter your email address"
                                                    disabled={disabledEmail}
                                                />}
                                            {!showLogin && (
                                                <Flex flexDirection="column">
                                                    <FieldText
                                                        name="first_name"
                                                        placeholder="First name"
                                                        width="100%"
                                                        label={label('Your first name', true)}
                                                        mr={[1, 3]}
                                                        mt={0}
                                                    />
                                                    <FieldText
                                                        name="last_name"
                                                        placeholder="Last name"
                                                        width="100%"
                                                        label={label('Your last name', true)}
                                                        mt={0}
                                                    />
                                                </Flex>
                                            )}
                                            <FieldText
                                                name="password"
                                                type="password"
                                                label={label(showLogin ? 'Your password' : 'Create a password', true)}
                                                placeholder="Enter your password"
                                                width="100%"
                                            />
                                        </Flex>
                                        <Text>
                                            {/* Own route (see auth/forgot-password.js) so refreshing keeps the
                                                    user on the reset page and the link is shareable. */}
                                            {showLogin && <RouterLink to="/password/forgot">
                                                Forgot Your Password?
                                            </RouterLink>}
                                        </Text>
                                    </Box>
                                </Box>
                                <Box bg="#F9F6FF" mb="6.7%" sx={{ display: ["none", "block", "block", "none"] }}>
                                    <Reviews reviews={userReviews} />
                                </Box>
                            </Flex>
                        </Wrapper >
                    </Form>
                )
            }}
        </Formik>

    );
};

export default SignUp;
